// Entities for the Compliance Suite module. All endpoints live under
// /api/v1/{buyer|supplier}/compliance/... and use the standard
// { status, data, count, message } response envelope.

import { ChecklistEvaluation, ChecklistExtraction } from './ComplianceChecklistTypes';

export const COMPLIANCE_REQUEST_LIFECYCLES = {
	DRAFT: 'Draft',
	PUBLISHED: 'Published',
};

export const COMPLIANCE_INSTANCE_STATUSES = {
	WAITING_ON_SUPPLIER: 'WaitingOnSupplier',
	PENDING_REVIEW: 'PendingReview',
	APPROVED: 'Approved',
	REJECTED: 'Rejected',
};

export const COMPLIANCE_EXPIRY_STATUSES = {
	UP_TO_DATE: 'UpToDate',
	EXPIRING_SOON: 'ExpiringSoon',
	EXPIRED: 'Expired',
};

export const COMPLIANCE_APPROVAL_MODES = {
	NON_COMPLIANT_UNTIL_APPROVED: 'NonCompliantUntilApproved',
	COMPLIANT_UNTIL_REJECTED: 'CompliantUntilRejected',
};

export const COMPLIANCE_ASSIGNMENT_MODES = {
	PROBLEM_TYPES: 'ProblemTypes',
	SUPPLIERS: 'Suppliers',
};

export interface ComplianceType {
	id: number;
	buyerCompanyId: number;
	name: string;
	countryCode: string;
	allowSampleUpload: boolean;
	requiresUniqueDocument: boolean;
	isLocationSpecific: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export interface ComplianceRequest {
	id: number;
	buyerCompanyId: number;
	complianceTypeId: number;
	complianceType?: ComplianceType;
	name: string;
	countryCode: string;
	isMandatory: boolean;
	dueBy?: string;
	requiresUniqueDocument: boolean;
	locationIds: number[];
	allLocations: boolean;
	assignmentMode: string;
	problemTypeIds: number[];
	allProblemTypes: boolean;
	supplierFacilityIds: number[];
	allSuppliers: boolean;
	notes?: string;
	sampleFileUrl?: string;
	// The checklist read from the sample document. Sent on create/update and
	// returned on reads; the backend stores it opaquely.
	checklistExtraction?: ChecklistExtraction;
	status: string;
	publishedAt?: string;
	// server-computed rollups on hydrated reads (with_instance_counts)
	instanceStatusCounts?: { [status: string]: number };
	targetedSupplierCount?: number;
	createdAt?: string;
	updatedAt?: string;
}

export interface ComplianceRequestInstance {
	id: number;
	complianceRequestId: number;
	complianceRequest?: ComplianceRequest;
	buyerCompanyId: number;
	buyerCompany?: { id: number; name: string };
	supplierFacilityId: number;
	supplierFacility?: { id: number; name: string };
	status: string;
	expiryStatus?: string;
	linkedComplianceDocumentId?: number;
	linkedComplianceDocument?: ComplianceDocument;
	// The AI evaluation of the linked document against the request's stored
	// `checklistExtraction`, persisted by link_document / replace_document and
	// cleared by unlink_document. The backend stores it opaquely.
	checklistEvaluation?: ChecklistEvaluation;
	reviewedByContactId?: string;
	reviewedAt?: string;
	rejectionReason?: string;
	createdAt?: string;
	updatedAt?: string;
}

export interface ComplianceDocument {
	id: number;
	supplierFacilityId: number;
	countryCode: string;
	complianceTypeId?: number;
	// Optional hydrated type, when the backend denormalizes it onto a read.
	complianceType?: ComplianceType;
	name: string;
	fileUrl: string;
	fileName?: string;
	// Explicit pre-authorization list of buyer companies that may auto-link this
	// document. An empty list means it is shared with nobody; there is no
	// "all clients" flag.
	buyerCompanyIds: number[];
	// Server-computed rollup on hydrated reads: per-status count of the client
	// request-instances this document is linked to (keyed by instance status).
	clientStatusCounts?: { [status: string]: number };
	// Validity window. Effective/expiry dates drive the derived expiry status shown
	// in the Documents tab (there is no server-side expiryStatus for documents).
	effectiveDate?: string;
	expiryDate?: string;
	// UI-only convenience flag some reads denormalize; buyerCompanyIds remains the
	// concrete pre-authorization list.
	sharedWithAllClients?: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export interface ComplianceSettings {
	id?: number;
	buyerCompanyId?: number;
	approvalMode: string;
	allowWorkOrdersForNonCompliant: boolean;
	createdAt?: string;
	updatedAt?: string;
}

export interface ComplianceSummary {
	totalSuppliers: number;
	nonCompliantSuppliers: number;
}
